import { useCallback, useMemo, useState } from 'react';
import type { HypothesisCreativeEntry, LaunchStatus } from '../types/hypothesis';

interface CreativeFiltersState {
  hypothesis: string[];
  customer: string | null;
  flow: string[];
  technology: string[];
  tpcFinishDate: string | null;
  status: LaunchStatus | null;
  search: string;
}

export interface FilterOptions {
  hypotheses: string[];
  customers: string[];
  flows: string[];
  technologies: string[];
}

interface UseCreativeFiltersReturn {
  filters: CreativeFiltersState;
  filterOptions: FilterOptions;
  filteredCreatives: HypothesisCreativeEntry[];
  setHypothesis: (val: string[]) => void;
  setCustomer: (val: string | null) => void;
  setFlow: (val: string[]) => void;
  setTechnology: (val: string[]) => void;
  setTpcFinishDate: (val: string | null) => void;
  setStatus: (val: LaunchStatus | null) => void;
  setSearch: (val: string) => void;
  resetFilters: () => void;
}

const DEFAULT_FILTERS: CreativeFiltersState = {
  hypothesis: [],
  customer: null,
  flow: [],
  technology: [],
  tpcFinishDate: null,
  status: null,
  search: '',
};

const useCreativeFilters = (
  creatives: HypothesisCreativeEntry[],
): UseCreativeFiltersReturn => {
  const [filters, setFilters] = useState<CreativeFiltersState>(DEFAULT_FILTERS);

  const filterOptions = useMemo<FilterOptions>(() => {
    const hypotheses = new Set<string>();
    const customers = new Set<string>();
    const flows = new Set<string>();
    const technologies = new Set<string>();

    for (const c of creatives) {
      if (c.hypothesis) hypotheses.add(c.hypothesis);
      if (c.customer) customers.add(c.customer);
      if (c.flow) flows.add(c.flow);
      if (c.technology) technologies.add(c.technology);
    }

    return {
      hypotheses: [...hypotheses].sort(),
      customers: [...customers].sort(),
      flows: [...flows].sort(),
      technologies: [...technologies].sort(),
    };
  }, [creatives]);

  const filteredCreatives = useMemo(() => {
    let result = creatives;

    if (filters.hypothesis.length > 0) {
      result = result.filter(
        (c) => c.hypothesis && filters.hypothesis.includes(c.hypothesis),
      );
    }

    if (filters.customer !== null) {
      result = result.filter((c) => c.customer === filters.customer);
    }

    if (filters.flow.length > 0) {
      result = result.filter(
        (c) => c.flow && filters.flow.includes(c.flow),
      );
    }

    if (filters.technology.length > 0) {
      result = result.filter(
        (c) => c.technology && filters.technology.includes(c.technology),
      );
    }

    if (filters.tpcFinishDate) {
      const filterDate = new Date(filters.tpcFinishDate);
      result = result.filter((c) => {
        if (!c.tpc_finish_date) return false;
        return new Date(c.tpc_finish_date) <= filterDate;
      });
    }

    if (filters.status) {
      result = result.filter((c) => c.launch_status === filters.status);
    }

    if (filters.search) {
      const query = filters.search.toLowerCase();
      result = result.filter((c) =>
        c.name.toLowerCase().includes(query) ||
        (c.hypothesis && c.hypothesis.toLowerCase().includes(query)),
      );
    }

    return result;
  }, [creatives, filters]);

  const setHypothesis = useCallback(
    (val: string[]) => { setFilters((prev) => ({ ...prev, hypothesis: val })); },
    [],
  );
  const setCustomer = useCallback(
    (val: string | null) => { setFilters((prev) => ({ ...prev, customer: val })); },
    [],
  );
  const setFlow = useCallback(
    (val: string[]) => { setFilters((prev) => ({ ...prev, flow: val })); },
    [],
  );
  const setTechnology = useCallback(
    (val: string[]) => { setFilters((prev) => ({ ...prev, technology: val })); },
    [],
  );
  const setTpcFinishDate = useCallback(
    (val: string | null) => { setFilters((prev) => ({ ...prev, tpcFinishDate: val })); },
    [],
  );
  const setStatus = useCallback(
    (val: LaunchStatus | null) => { setFilters((prev) => ({ ...prev, status: val })); },
    [],
  );
  const setSearch = useCallback(
    (val: string) => { setFilters((prev) => ({ ...prev, search: val })); },
    [],
  );
  const resetFilters = useCallback(() => { setFilters(DEFAULT_FILTERS); }, []);

  return {
    filters,
    filterOptions,
    filteredCreatives,
    setHypothesis,
    setCustomer,
    setFlow,
    setTechnology,
    setTpcFinishDate,
    setStatus,
    setSearch,
    resetFilters,
  };
};

export default useCreativeFilters;
